import { Guid, IApiLink } from '@/types';
import Axios from 'axios';
import { Currency, ErrorCode } from 'config/constants';
import AmountWithCurrency from 'models/AmountWithCurrency';
import CustomError from 'models/CustomError';
import { FairValueReferenceNumberEvaluationItem } from 'models/FairValueEvaluation';

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import FinanceService from 'services/FinanceService';
import toastMessages from 'stores/toastMessages';

export default function useBookValues(
    link: IApiLink | undefined,
    fairValueEvaluations: FairValueReferenceNumberEvaluationItem[] | null
) {

    const fetchBookValuesAbortController = useRef<AbortController | null>(null)
    const assetIdsRef = useRef<Guid[]>([]);
    const isLoadingPortionRef = useRef(false);

    const [data, setData] = useState<Record<Guid, AmountWithCurrency | null | undefined>>({})

    useEffect(() => {
        const assetIds = fairValueEvaluations?.reduce<Guid[]>((acc, evaluation) => {
            const _assetIds = evaluation.entities.map(e => e.assetId).filter(id => !!id) as Guid[]
            return [...acc, ..._assetIds]
        }, [] as Guid[]) ?? []
        assetIdsRef.current = assetIds;
        const newPortion = assetIds.reduce((acc, assetId) => ({ ...acc, [assetId]: undefined }), {})
        setData(prev => ({ ...newPortion, ...prev, }));
    }, [fairValueEvaluations]);



    const fetchBookValues = useCallback(async (assetIds) => {
        if (!link) return
        isLoadingPortionRef.current = true
        if (fetchBookValuesAbortController.current) {
            fetchBookValuesAbortController.current.abort()
        }

        fetchBookValuesAbortController.current = new AbortController()

        try {
            const bookValues = await FinanceService.getFairValueEvaluationBookValues(link, assetIds, fetchBookValuesAbortController.current.signal)
            const dataPortion = bookValues.reduce((acc, { assetId, bookValue }) => ({ ...acc, [assetId]: bookValue ?? null }), {})
            setData(prev => ({ ...prev, ...dataPortion }))
        } catch (e) {
            // Don't show error for cancelled requests - this is expected when new data loads
            const isCancelled = Axios.isCancel(e) || (e instanceof CustomError && e.code === ErrorCode.requestCanceled)
            if (!isCancelled) {
                toastMessages.showSystemError()
            }
        }

        isLoadingPortionRef.current = false
    }, [link])

    useEffect(() => {
        isLoadingPortionRef.current = false
    }, [link])

    useEffect(() => {
        if (isLoadingPortionRef.current) return
        const notLoadedAssets = assetIdsRef.current.filter(assetId => data[assetId] === undefined).slice(0, 50)

        if (notLoadedAssets.length > 0) {
            fetchBookValues(notLoadedAssets)
        }


    }, [data, fetchBookValues])


    const referenceNumbersBookValues = useMemo<Record<Guid, AmountWithCurrency | null | undefined>>(

        () => {
            if (!fairValueEvaluations) {
                return {}
            }
            const result = {}
            fairValueEvaluations.forEach(evaluation => {
                let amount: number | null | undefined
                let currency: Currency | null = null

                for (const entity of evaluation.entities) {
                    if (!entity.assetId) continue

                    const value = data[entity.assetId]
                    if (value === undefined) {
                        amount = undefined
                        break
                    }
                    if (value) {
                        amount = (amount ?? 0) + value.amount
                        currency = value.currency
                    }
                }
                if (amount !== undefined && amount !== null && currency) {
                    result[evaluation.id] = new AmountWithCurrency({ amount, currency })
                } else return amount


            })


            return result
        },

        [fairValueEvaluations, data])

    return {
        assetBookValues: data,
        referenceNumbersBookValues
    }
}
